import React, { useState, useRef, useEffect } from 'react';
import { B } from '../constants';
import { borderRadius } from '../designTokens';
import { GBIcon } from './Logo';
import { ai as aiAPI } from '../api';
import CityProse from './CityProse';
import PlaceSheet, { categoryLabel } from './PlaceSheet';
import { PlaceCards, PlacesMap } from './ChatPlaces';
import './AIChatWidget.css';

const QUICK_CHIPS = [
  { emoji: '\uD83C\uDFD6\uFE0F', text: 'Beach getaway under $1500' },
  { emoji: '\uD83C\uDFD9\uFE0F', text: '5 days in Tokyo' },
  { emoji: '\uD83C\uDF34', text: 'Best time to visit Bali' },
];

const KNOWN_DESTINATIONS = [
  'Bali', 'Paris', 'Tokyo', 'New York', 'Santorini', 'Dubai', 'London', 'Rome',
  'Barcelona', 'Bangkok', 'Maldives', 'Swiss Alps', 'Iceland', 'Sydney',
  'Marrakech', 'Lisbon', 'Amsterdam', 'Berlin', 'Prague', 'Vienna', 'Istanbul',
  'Kyoto', 'Osaka', 'Seoul', 'Singapore', 'Hong Kong', 'Hanoi', 'Ho Chi Minh',
  'Phuket', 'Chiang Mai', 'Kuala Lumpur', 'Jakarta', 'Taipei',
  'Mumbai', 'Delhi', 'Goa', 'Jaipur', 'Kerala', 'Cape Town', 'Marrakesh',
  'Cairo', 'Nairobi', 'Zanzibar', 'Athens', 'Mykonos', 'Crete', 'Florence',
  'Venice', 'Milan', 'Naples', 'Madrid', 'Seville', 'Porto', 'Dublin',
  'Edinburgh', 'Reykjavik', 'Oslo', 'Stockholm', 'Copenhagen', 'Helsinki',
  'Budapest', 'Krakow', 'Warsaw', 'Zurich', 'Geneva', 'Munich', 'Hamburg',
  'Buenos Aires', 'Rio de Janeiro', 'Lima', 'Cusco', 'Machu Picchu',
  'Mexico City', 'Cancun', 'Tulum', 'Costa Rica', 'Patagonia', 'Galapagos',
  'Toronto', 'Vancouver', 'Montreal', 'Banff', 'Los Angeles', 'San Francisco',
  'Las Vegas', 'Miami', 'Chicago', 'Seattle', 'Boston', 'Hawaii', 'Maui',
  'Honolulu',
];

function extractDestinationFromReply(reply) {
  if (!reply || typeof reply !== 'string') return null;
  const lower = reply.toLowerCase();
  for (const name of KNOWN_DESTINATIONS) {
    const re = new RegExp(`\\b${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').toLowerCase()}\\b`);
    if (re.test(lower)) return name;
  }
  return null;
}

export default function AIChatWidget({ user, onAuthRequired, goScreen }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      text: "Hi! I'm your travel assistant \uD83C\uDF0D Where are you dreaming of going?",
      isUser: false,
    },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [openPlace, setOpenPlace] = useState(null);
  const [mapFor, setMapFor] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && !e.target.closest('[data-widget-toggle]')) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const sendMessage = async (text) => {
    if (!text.trim()) return;
    if (!user) { onAuthRequired(); return; }

    const userMsg = { id: Date.now(), text: text.trim(), isUser: true };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setIsTyping(true);

    try {
      const context = updatedMessages.slice(-6).map(m => `${m.isUser ? 'User' : 'Assistant'}: ${m.text}`).join('\n');
      const data = await aiAPI.chat(text.trim(), context);
      const destination = data.city?.name || extractDestinationFromReply(data.reply);
      const places = Array.isArray(data.places) ? data.places : [];
      setMessages(prev => [...prev, { id: Date.now() + 1, text: data.reply, isUser: false, destination, city: data.city || null, places }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        text: "Sorry, I couldn't process that right now. Please try again in a moment.",
        isUser: false,
      }]);
    } finally {
      setIsTyping(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const resetChat = () => {
    setMessages([{
      id: 'welcome',
      text: "Hi! I'm your travel assistant \uD83C\uDF0D Where are you dreaming of going?",
      isUser: false,
    }]);
    setInput('');
  };

  return (
    <>
      {isOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 998, background: 'rgba(0,0,0,0.3)',
          animation: 'fadeIn 0.2s ease',
        }} onClick={() => setIsOpen(false)} />
      )}

      <div ref={panelRef} className={isOpen ? 'chat-widget-panel-open' : ''} style={{
        position: 'fixed', bottom: isOpen ? 90 : -500, right: 20, zIndex: 999,
        width: 380, maxWidth: 'calc(100vw - 40px)',
        background: '#fff', borderRadius: borderRadius.lg,
        boxShadow: '0 8px 40px rgba(15,25,35,0.2)',
        border: '1px solid #e7e2d9',
        overflow: 'hidden',
        transition: 'bottom 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.2s ease',
        opacity: isOpen ? 1 : 0,
        pointerEvents: isOpen ? 'auto' : 'none',
      }}>
        <div style={{
          background: B.night, color: '#fff', padding: '12px 16px',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <GBIcon size={28} />
          <div style={{ flex: 1 }}>
            <b style={{ fontSize: 14 }}>GreatBreak AI</b>
            <div style={{ fontSize: 11, opacity: 0.7 }}>Travel assistant</div>
          </div>
          <button onClick={resetChat} title="New chat"
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, padding: '4px 6px', opacity: 0.7 }}>
            {'\uD83D\uDD04'}
          </button>
          <button onClick={() => setIsOpen(false)} title="Close"
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 18, padding: '4px 6px', opacity: 0.7 }}>
            {'\u2715'}
          </button>
        </div>

        <div style={{
          padding: 16, minHeight: 200, maxHeight: 'min(460px, calc(100vh - 230px))', overflowY: 'auto',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          {messages.map(msg => (
            <React.Fragment key={msg.id}>
              {msg.isUser ? (
                <div style={{
                  maxWidth: '85%', padding: '10px 14px', borderRadius: 14,
                  background: B.forest, color: '#fff', alignSelf: 'flex-end',
                  borderBottomRightRadius: 4, fontSize: 13, lineHeight: 1.5,
                  wordBreak: 'break-word',
                }}>
                  {msg.text}
                </div>
              ) : (
                <div style={{
                  maxWidth: msg.places?.length ? '100%' : '85%', padding: '10px 14px', borderRadius: 14,
                  background: B.sand, border: '1px solid #e7e2d9', alignSelf: 'flex-start',
                  borderBottomLeftRadius: 4, fontSize: 13, lineHeight: 1.5,
                  wordBreak: 'break-word', minWidth: 0,
                }}>
                  <CityProse text={String(msg.text || '').replace(/^\s*([-*_])\1{2,}\s*$/gm, '')} style={{ fontSize: 13, lineHeight: 1.5, margin: '0 0 6px' }} />
                  {msg.places?.length > 0 && (
                    <>
                      <PlaceCards places={msg.places} onOpen={(p) => setOpenPlace({ place: p, city: msg.city })} onHover={() => {}} />
                      <button type="button" onClick={() => setMapFor(mapFor === msg.id ? null : msg.id)}
                        style={{
                          marginTop: 8, border: 'none', background: 'none', color: B.forest, fontWeight: 700,
                          fontSize: 12, cursor: 'pointer', padding: 0, fontFamily: "'Montserrat',sans-serif",
                        }}>
                        {mapFor === msg.id ? 'Hide map' : `\uD83D\uDCCD Show ${msg.places.length} on map`}
                      </button>
                      {mapFor === msg.id && (
                        <div style={{ marginTop: 8 }} ref={(el) => el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })}>
                          <PlacesMap city={msg.city} places={msg.places} hovered={null} height={200}
                            onSelect={(p) => p && setOpenPlace({ place: p, city: msg.city })} />
                        </div>
                      )}
                    </>
                  )}
                  {msg.text === messages[messages.length - 1]?.text && msg.destination && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                      <button onClick={() => { setIsOpen(false); goScreen('ai', { prefillDestination: msg.destination }); }}
                        style={{
                          border: '1.5px solid #1a6b4a', color: B.forest, background: '#fff',
                          borderRadius: borderRadius.pill, padding: '5px 12px', fontSize: 12, fontWeight: 600,
                          cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
                        }}>
                        {'\u2728'} Build full itinerary
                      </button>
                      <button onClick={() => { setIsOpen(false); goScreen('home'); }}
                        style={{
                          border: '1.5px solid #1a6b4a', color: B.forest, background: '#fff',
                          borderRadius: borderRadius.pill, padding: '5px 12px', fontSize: 12, fontWeight: 600,
                          cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
                        }}>
                        {'\uD83D\uDD0D'} Show destinations
                      </button>
                    </div>
                  )}
                </div>
              )}
            </React.Fragment>
          ))}

          {isTyping && (
            <div style={{
              maxWidth: '85%', padding: '10px 14px', borderRadius: 14,
              background: B.sand, border: '1px solid #e7e2d9', alignSelf: 'flex-start',
              borderBottomLeftRadius: 4, fontSize: 16, lineHeight: 1, color: B.muted,
            }}>
              {'\u2026'}
            </div>
          )}

          {messages.length <= 1 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 2 }}>
              {QUICK_CHIPS.map((chip, i) => (
                <button key={i} onClick={() => sendMessage(chip.text)}
                  style={{
                    border: '1.5px solid #1a6b4a', color: B.forest, background: '#fff',
                    borderRadius: borderRadius.pill, padding: '5px 12px', fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
                  }}>
                  {chip.emoji} {chip.text}
                </button>
              ))}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div style={{ display: 'flex', gap: 8, borderTop: '1px solid #e7e2d9', padding: '10px 12px' }}>
          <input ref={inputRef}
            value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={user ? "Type a message..." : "Sign in to chat"}
            disabled={!user || isTyping}
            style={{
              flex: 1, border: '1px solid #e7e2d9', borderRadius: borderRadius.pill,
              padding: '10px 14px', fontFamily: "'Montserrat',sans-serif",
              fontSize: 13, outline: 'none', background: '#fff',
            }} />
          <button onClick={() => sendMessage(input)}
            disabled={!input.trim() || isTyping || !user}
            style={{
              background: input.trim() && !isTyping && user ? B.forest : B.muted,
              color: '#fff', border: 'none', borderRadius: borderRadius.pill,
              padding: '10px 18px', fontWeight: 700, fontSize: 13,
              cursor: input.trim() && !isTyping && user ? 'pointer' : 'not-allowed',
              fontFamily: "'Montserrat',sans-serif",
            }}>
            Send
          </button>
        </div>
      </div>

      {openPlace && (
        <PlaceSheet place={openPlace.place} cityName={openPlace.city?.name} categoryLabel={categoryLabel}
          center={openPlace.city ? { lat: openPlace.city.lat, lng: openPlace.city.lng } : null}
          onClose={() => setOpenPlace(null)} />
      )}

      <button data-widget-toggle
        className="chat-widget-fab"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Close chat' : 'Open AI chat'}
        style={{
          position: 'fixed', bottom: 24, right: 20, zIndex: 997,
          width: 56, height: 56, borderRadius: '50%',
          background: `linear-gradient(135deg, ${B.forest}, ${B.mint})`,
          border: 'none', boxShadow: '0 4px 20px rgba(26,107,74,0.4)',
          cursor: 'pointer', display: 'grid', placeItems: 'center',
          transition: 'transform 0.2s ease',
          fontSize: 24,
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
        {isOpen ? '\u2715' : '\uD83D\uDCAC'}
      </button>
    </>
  );
}
